// --- START PATCH: Equalize open-card heights (sm+), mirror pt-10 in clones, match typography, and re-measure only on width changes to eliminate shifting ---
import {
  useEffect,
  useMemo,
  useState,
  useRef,
  useLayoutEffect,
  type ReactNode,
} from "react";
import { useMaterials } from "@/data/useMaterials";
import { Card } from "@/lib/components/ui/card";
import { Badge } from "@/lib/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/lib/components/ui/accordion";
import { setMetaDescription, setPageTitle } from "@/lib/seo";
import {
  Gauge,
  Thermometer,
  SunMedium,
  CircleDollarSign,
  Leaf,
  Shield,
  Waves,
} from "lucide-react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
// --- END PATCH: Equalize open-card heights with i18n ---

// Mirrors the visible spacing above "Best for" (Tailwind pt-10 = 2.5rem)
const BEST_FOR_TOP_REM = 2.5;

const iconForMaterial = (id: string) => {
  switch (id) {
    case "PLA":
      return <Leaf className="h-5 w-5 text-emerald-400" aria-hidden="true" />;
    case "PETG":
      return <Shield className="h-5 w-5 text-blue-400" aria-hidden="true" />;
    case "ASA":
      return <SunMedium className="h-5 w-5 text-yellow-400" aria-hidden="true" />;
    case "TPU":
      return <Waves className="h-5 w-5 text-indigo-400" aria-hidden="true" />;
    default:
      return null;
  }
};

function TraitBadge({
  label,
  value,
  title,
  children,
}: {
  label: string;
  value: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <Badge
      variant="secondary"
      className="inline-flex items-center gap-1.5"
      title={title}
      aria-label={`${label}: ${value}`}
    >
      {children}
      <span className="font-medium">{label}:</span>
      <span className="opacity-90 truncate">{value}</span>
    </Badge>
  );
}

// sm+ media query hook
function useSmUp() {
  const [smUp, setSmUp] = useState<boolean>(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 640px)").matches
      : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(min-width: 640px)");
    const listener = (e: MediaQueryListEvent) => setSmUp(e.matches);
    mql.addEventListener("change", listener);
    setSmUp(mql.matches);
    return () => mql.removeEventListener("change", listener);
  }, []);
  return smUp;
}

export default function MaterialGuide() {
  const { t } = useTranslation('marketing');
  const materials = useMaterials();
  
  useEffect(() => {
    setPageTitle(t('materialGuide.title') + " • 3D Printing");
    setMetaDescription(t('materialGuide.description'));
  }, [t]);

  const ordered = useMemo(() => [...materials].sort((a, b) => a.order - b.order), [materials]);

  // Open state per-card
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});
  // Show Best for only after open animation completes (unless reduced motion)
  const [openReadyMap, setOpenReadyMap] = useState<Record<string, boolean>>({});
  const prefersReducedMotion = usePrefersReducedMotion();

  // Measurement: canonical open height for sm+ (USE TALLEST across all cards)
  const gridRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const measureRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // Refs used to apply exact "Best for" top padding in the measurement clone
  const measureGapRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const setMeasureGapRef = (id: string) => (el: HTMLDivElement | null) => {
    measureGapRefs.current[id] = el;
  };

  const baseHeightsAtMount = useRef<Record<string, number>>({});
  const [canonicalOpenHeight, setCanonicalOpenHeight] = useState<number>(0);
  const smUp = useSmUp();

  // Track grid width to avoid re-measuring on height-only changes (e.g., animations)
  const lastGridWidth = useRef<number>(0);

  const setCardRef = (id: string) => (el: HTMLDivElement | null) => {
    cardRefs.current[id] = el;
  };
  const setMeasureRef = (id: string) => (el: HTMLDivElement | null) => {
    measureRefs.current[id] = el;
  };

  // Compute canonical height as the TALLEST expanded height across all cards (sm+),
  // and mirror the visible pt-10 in EVERY measurement clone so canonical includes that space.
  const doMeasure = () => {
    if (!smUp) {
      setCanonicalOpenHeight(0);
      return;
    }

    // Compute pt-10 in px based on current root font size (rem)
    const rootFontSizePx = parseFloat(
      window.getComputedStyle(document.documentElement).fontSize || "16"
    );
    const bestForTopPx = Math.round(rootFontSizePx * BEST_FOR_TOP_REM);

    let maxOpenHeight = 0;

    for (const m of ordered) {
      const id = m.id;
      const cardEl = cardRefs.current[id];
      const measureEl = measureRefs.current[id];
      if (!cardEl || !measureEl) continue;

      // Record each card's closed height at first measure
      if (baseHeightsAtMount.current[id] == null) {
        baseHeightsAtMount.current[id] = cardEl.clientHeight;
      }
      const base = baseHeightsAtMount.current[id] || 0;

      // Mirror visible "Best for" top padding (pt-10) in the measurement clone
      const gapEl = measureGapRefs.current[id];
      if (gapEl) {
        gapEl.style.paddingTop = `${bestForTopPx}px`;
      }

      // Height of details + best-for measurement clone
      const extra = measureEl.scrollHeight || 0;
      const openHeight = base + extra;

      if (openHeight > maxOpenHeight) {
        maxOpenHeight = openHeight;
      }
    }

    // Monotonic: never decrease (avoids jitter on minor layout noise)
    setCanonicalOpenHeight((prev) => Math.max(prev, maxOpenHeight || 0));
  };

  // Measure in a stable frame (after layout), and only when needed
  const measureStable = () => {
    requestAnimationFrame(() => {
      doMeasure();
    });
  };

  useLayoutEffect(() => {
    const measureAfterFonts = () => {
      // Initialize width baseline, then measure
      lastGridWidth.current = gridRef.current?.clientWidth || 0;
      measureStable();
    };

    if (typeof document !== "undefined") {
      const fonts: FontFaceSet | undefined = (document as Document).fonts;
      if (fonts && typeof fonts.ready?.then === "function") {
        fonts.ready.then(() => measureAfterFonts()).catch(() => measureAfterFonts());
      } else {
        measureAfterFonts();
      }
    } else {
      measureAfterFonts();
    }

    // Only re-measure when WIDTH changes (text wrapping can change true heights)
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined" && gridRef.current) {
      ro = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect?.width ?? gridRef.current!.clientWidth;
        if (typeof width === "number" && Math.abs(width - lastGridWidth.current) > 0.5) {
          lastGridWidth.current = width;
          measureStable();
        }
      });
      ro.observe(gridRef.current);
    }

    // Guard for browsers that may not propagate width via RO
    const onResize = () => {
      const width = gridRef.current?.clientWidth || 0;
      if (Math.abs(width - lastGridWidth.current) > 0.5) {
        lastGridWidth.current = width;
        measureStable();
      }
    };
    if (typeof window !== "undefined") {
      window.addEventListener("resize", onResize);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", onResize);
      }
      if (ro) ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smUp, ordered.length]);

  const gridClass =
    "max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 items-start";

  return (
    <div className="min-h-[calc(100vh-64px)] bg-muted text-foreground">
      {/* Hero */}
      <section className="px-6 pt-12 pb-8">
        <div className="max-w-6xl mx-auto text-center">
          {/* --- START EDIT: Use translations for hero --- */}
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
            {t('materialGuide.title')}
          </h1>
          <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
            {t('materialGuide.description')}
          </p>
          {/* --- END EDIT: Use translations for hero --- */}
        </div>
      </section>

      {/* Legend */}
      <section className="px-6 pb-4">
        <div className="max-w-6xl mx-auto">
          <Card className="p-4 bg-card/80 backdrop-blur">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <Gauge className="h-4 w-4 mt-0.5" aria-hidden="true" />
                <div>
                  <div className="font-medium text-foreground">{t('materialGuide.legendItems.ease')}</div>
                  <div>{t('materialGuide.legendItems.easeDesc')}</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Thermometer className="h-4 w-4 mt-0.5" aria-hidden="true" />
                <div>
                  <div className="font-medium text-foreground">{t('materialGuide.legendItems.heat')}</div>
                  <div>{t('materialGuide.legendItems.heatDesc')}</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <SunMedium className="h-4 w-4 mt-0.5" aria-hidden="true" />
                <div>
                  <div className="font-medium text-foreground">{t('materialGuide.legendItems.uv')}</div>
                  <div>{t('materialGuide.legendItems.uvDesc')}</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CircleDollarSign className="h-4 w-4 mt-0.5" aria-hidden="true" />
                <div>
                  <div className="font-medium text-foreground">{t('materialGuide.legendItems.cost')}</div>
                  <div>{t('materialGuide.legendItems.costDesc')}</div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Cards Grid */}
      <section className="px-6 pb-12">
        <div ref={gridRef} className={gridClass}>
          {ordered.map((m) => {
            const isOpen = Boolean(openMap[m.id]);

            // On sm+, opened cards get the canonical tallest height
            const stretch = isOpen;
            const openMinHeight =
              smUp && canonicalOpenHeight > 0 ? canonicalOpenHeight : undefined;

            // Show Best-for only after opening animation ends (or immediately with reduced motion)
            const bestForVisible = prefersReducedMotion
              ? isOpen
              : Boolean(isOpen && openReadyMap[m.id]);

            return (
              <Card
                key={m.id}
                ref={setCardRef(m.id)}
                className={
                  "relative p-5 bg-card/90 backdrop-blur border-border flex flex-col " +
                  (stretch ? "self-stretch h-full" : "")
                }
                style={
                  stretch && openMinHeight
                    ? { minHeight: openMinHeight }
                    : undefined
                }
              >
                {/* Hidden measurement clone: mirrors Details + (gap) + Best for, without affecting layout */}
                <div
                  ref={setMeasureRef(m.id)}
                  aria-hidden="true"
                  className="invisible absolute left-0 top-0 w-full pointer-events-none"
                >
                  {/* IMPORTANT: text-sm to match visible AccordionContent typography */}
                  <div className="text-sm text-muted-foreground">
                    <p className="mb-3">
                      {m.description}
                    </p>
                    {/* Mirror bottom padding used inside AccordionContent below */}
                    <div className="flex flex-col gap-4 pb-[2px]">
                      <div>
                        <h3 className="font-medium text-foreground">{t('materialGuide.pros')}</h3>
                        <ul className="mt-2 list-disc list-inside space-y-1">
                          {m.pros.map((p, idx) => (
                            <li key={idx}>{p}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-medium text-foreground">{t('materialGuide.cons')}</h3>
                        <ul className="mt-2 list-disc list-inside space-y-1">
                          {m.cons.map((c, idx) => (
                            <li key={idx}>{c}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    {/* Gap before Best for mirrors visible pt-10 in EVERY clone to match real card height */}
                    <div ref={setMeasureGapRef(m.id)} style={{ paddingTop: 0 }}>
                      <h3 className="font-medium text-foreground">{t('materialGuide.bestFor')}</h3>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {m.bestFor.map((b, idx) => (
                          <Badge key={idx} variant="outline">
                            {b}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Header */}
                <div className="flex items-center gap-2">
                  {iconForMaterial(m.id)}
                  <h2 className="text-xl font-semibold">{m.name}</h2>
                </div>
                <p className="mt-2 text-sm text-muted-foreground">{m.summary}</p>

                {/* Traits */}
                <div className="mt-4 grid grid-cols-1 gap-2">
                  <TraitBadge
                    label={t('materialGuide.traitLabels.ease')}
                    value={m.traits.easeOfPrinting}
                    title={t('materialGuide.traitTitles.ease')}
                  >
                    <Gauge className="h-4 w-4" aria-hidden="true" />
                  </TraitBadge>
                  <TraitBadge
                    label={t('materialGuide.traitLabels.heat')}
                    value={m.traits.heatResistance}
                    title={t('materialGuide.traitTitles.heat')}
                  >
                    <Thermometer className="h-4 w-4" aria-hidden="true" />
                  </TraitBadge>
                  <TraitBadge
                    label={t('materialGuide.traitLabels.uv')}
                    value={m.traits.uvResistance}
                    title={t('materialGuide.traitTitles.uv')}
                  >
                    <SunMedium className="h-4 w-4" aria-hidden="true" />
                  </TraitBadge>
                  <TraitBadge
                    label={t('materialGuide.traitLabels.cost')}
                    value={m.traits.cost}
                    title={t('materialGuide.traitTitles.cost')}
                  >
                    <CircleDollarSign className="h-4 w-4" aria-hidden="true" />
                  </TraitBadge>
                </div>

                {/* Details + Best for */}
                <div className="mt-4 flex-1 flex flex-col">
                  <Accordion
                    type="single"
                    collapsible
                    value={isOpen ? "details" : ""}
                    onValueChange={(val) => {
                      const opening = val === "details";
                      setOpenMap((prev) => ({ ...prev, [m.id]: opening }));
                      // when opening, wait for animation unless reduced motion; when closing, hide immediately
                      setOpenReadyMap((prev) => ({
                        ...prev,
                        [m.id]: prefersReducedMotion ? opening : false,
                      }));
                    }}
                  >
                    <AccordionItem value="details">
                      <AccordionTrigger className="text-sm">{t('materialGuide.details')}</AccordionTrigger>
                      {/* Match clone typography so measured and real heights align */}
                      {/* --- START EDIT: add text-sm so visible content matches the measurement clone exactly --- */}
                      <AccordionContent
                        className="text-sm text-muted-foreground pb-[2px]"
                        onAnimationEnd={(e: React.AnimationEvent<HTMLDivElement>) => {
                          if (
                            e.animationName &&
                            e.animationName.includes("accordion-down")
                          ) {
                            // opening finished; now we can show Best-for
                            setOpenReadyMap((prev) => ({ ...prev, [m.id]: true }));
                          }
                        }}
                      >
                      {/* --- END EDIT --- */}
                        <p className="mb-3">{m.description}</p>
                        <div className="flex flex-col gap-4">
                          <div>
                            <h3 className="font-medium text-foreground">{t('materialGuide.pros')}</h3>
                            <ul className="mt-2 list-disc list-inside space-y-1">
                              {m.pros.map((p, idx) => (
                                <li key={idx}>{p}</li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h3 className="font-medium text-foreground">{t('materialGuide.cons')}</h3>
                            <ul className="mt-2 list-disc list-inside space-y-1">
                              {m.cons.map((c, idx) => (
                                <li key={idx}>{c}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  {/* Best for: bottom-anchored; visible only after open animation completes (or immediately with reduced motion) */}
                  {bestForVisible ? (
                    // Keep your visible spacing (pt-10). This is mirrored in the clone to prevent growth.
                    <div className="mt-auto pt-10">
                      <h3 className="font-medium text-foreground">{t('materialGuide.bestFor')}</h3>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {m.bestFor.map((b, idx) => (
                          <Badge key={idx} variant="outline">
                            {b}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Comparison Table */}
      <section className="px-6 pb-16">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold mb-4">{t('materialGuide.table.title')}</h2>
          <div className="overflow-x-auto rounded-md border border-border bg-card/60">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/60">
                <tr className="text-left">
                  <th className="px-4 py-3 font-medium">{t('materialGuide.table.material')}</th>
                  <th className="px-4 py-3 font-medium">{t('materialGuide.table.heatResistance')}</th>
                  <th className="px-4 py-3 font-medium">{t('materialGuide.table.strength')}</th>
                  <th className="px-4 py-3 font-medium">{t('materialGuide.table.flexibility')}</th>
                  <th className="px-4 py-3 font-medium">{t('materialGuide.table.uvWeather')}</th>
                  <th className="px-4 py-3 font-medium">{t('materialGuide.table.easeOfPrinting')}</th>
                  <th className="px-4 py-3 font-medium">{t('materialGuide.table.cost')}</th>
                </tr>
              </thead>
              <tbody>
                {ordered.map((m) => (
                  <tr key={m.id} className="border-t border-border">
                    <td className="px-4 py-3 font-medium">{m.name}</td>
                    <td className="px-4 py-3">{m.traits.heatResistance}</td>
                    <td className="px-4 py-3">{m.traits.strength}</td>
                    <td className="px-4 py-3">{m.traits.flexibility}</td>
                    <td className="px-4 py-3">{m.traits.uvResistance}</td>
                    <td className="px-4 py-3">{m.traits.easeOfPrinting}</td>
                    <td className="px-4 py-3">{m.traits.cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {t('materialGuide.table.footer')}
          </p>
        </div>
      </section>
    </div>
  );
}
// --- END PATCH: Equalize open-card heights (sm+), mirror pt-10 in clones, match typography, and re-measure only on width changes to eliminate shifting ---